"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";

import { getPlatformIcon } from "../utils/platformIcons";
import { Platform } from "../types/";
import { useAppContext } from "@/context/AppContext";
import { AnalyticsSkeleton } from "@/components/Skeletons";

interface AnalyticsItem {
  platform: Platform;
  page: {
    id: string;
    name: string;
    username?: string;
    category?: string;
    followers?: number;
    subscribers?: number;
    totalViews?: number;
    following?: number;
    [key: string]: any;
  };
  summary: {
    likes?: number;
    comments?: number;
    shares?: number;
    views?: number;
    engagement?: number;
    clicks?: number;
    video_count?: number;
    [key: string]: any;
  };
  performance?: {
    engagementRate?: number;
    avgViewsPerPost?: number;
    [key: string]: any;
  };
  top_posts: {
    posts: Array<{
      id: string;
      title: string;
      likes?: number;
      comments?: number;
      shares?: number;
      views?: number;
      engagement?: number;
      created_time?: string;
      publishedAt?: string;
      media_type?: string;
      [key: string]: any;
    }>;
  };
  insights?: any[];
}

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, fetchAnalytics } = useAppContext();

  const analyticsList: AnalyticsItem[] = state.analyticsList || [];
  const loading = state.analyticsLoading;

  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [pageLoading, setPageLoading] = useState(true);

  const platforms: Platform[] = [
    "facebook",
    "instagram",
    "linkedin",
    "tiktok",
    "youtube",
  ];

  const platformParam = (searchParams.get("platform") || "") as Platform;

  const platformsWithDataSet = useMemo(
    () =>
      new Set(
        analyticsList
          .filter((a) => a.platform && a.page?.id)
          .map((a) => a.platform)
      ),
    [analyticsList]
  );

  const selectedPlatform = useMemo<Platform | null>(() => {
    if (platformParam && platformsWithDataSet.has(platformParam)) {
      return platformParam;
    }
    return (Array.from(platformsWithDataSet)[0] as Platform) ?? null;
  }, [platformParam, platformsWithDataSet]);

  const analytics = useMemo(() => {
    return analyticsList.find((a) => a.platform === selectedPlatform) ?? null;
  }, [analyticsList, selectedPlatform]);

  const topPosts = analytics?.top_posts?.posts || [];

  useEffect(() => {
    if (
      !loading &&
      selectedPlatform &&
      searchParams.get("platform") !== selectedPlatform
    ) {
      setSearchParams({ platform: selectedPlatform }, { replace: true });
    }
  }, [loading, selectedPlatform, searchParams, setSearchParams]);

  useEffect(() => {
    if (loading) {
      setPageLoading(true);
      return;
    }

    if (analytics) {
      const timer = setTimeout(() => {
        setPageLoading(false);
      }, 150);

      return () => clearTimeout(timer);
    }

    setPageLoading(true);
  }, [loading, analytics]);

  const getAudienceInfo = () => {
    if (!analytics?.page) return { count: 0, label: t("followers") };

    switch (selectedPlatform) {
      case "youtube":
        return {
          count: analytics?.page.subscribers ?? 0,
          label: t("subscribers"),
        };
      case "tiktok":
      case "instagram":
      case "facebook":
        return { count: analytics?.page.followers ?? 0, label: t("followers") };
      default:
        return { count: 0, label: t("audience") };
    }
  };

  if ( loading ) {
    
    return <AnalyticsSkeleton />;
  }

  const { count: audienceCount, label: audienceLabel } = getAudienceInfo();

  return (
    <div className="mt-5">
      <main className="w-full flex flex-col gap-y-3 px-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">
              {selectedPost ? t("post_details") : t("analytics")}
            </h1>
            {selectedPlatform && (
              <span className="hidden sm:inline-flex px-3 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-700">
                {selectedPlatform.charAt(0).toUpperCase() +
                  selectedPlatform.slice(1)}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {platforms.map((p) => {
            const IconComponent = getPlatformIcon(p);
            const active = selectedPlatform === p;
            const hasData = platformsWithDataSet.has(p);

            return (
              <button
                key={p}
                disabled={!hasData}
                onClick={() => {
                  if (!hasData) return;
                  setSelectedPost(null);
                  setSearchParams({ platform: p }, { replace: true });
                }}
                className={`relative p-1 rounded-full transition-all
                  ${
                    hasData
                      ? "hover:scale-105"
                      : "opacity-40 cursor-not-allowed grayscale"
                  }
                  ${active && hasData ? "ring-4 ring-blue-300 shadow-md" : ""}
                `}
              >
                <div className="md:w-12 md:h-12 w-10 h-10 rounded-full flex items-center justify-center shadow-md">
                  {IconComponent ? (
                    <IconComponent className="w-8 h-8" />
                  ) : (
                    p.slice(0, 2)
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="p-2 border border-gray-200 flex gap-2 justify-between items-center w-full rounded-md bg-white shadow-sm">
          <p className="text-xs text-purple-600">
            Refreshes Automatically Every 24 to 48 Hours
          </p>

          <button
            disabled={state.analyticsLoading}
            onClick={() => fetchAnalytics(true)}
            className={`flex items-center justify-center
              ${
                state.analyticsLoading
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer"
              }
            `}
          >
            <RefreshCw
              className={`w-4 h-4 text-purple-600 transition-colors
                ${
                  state.analyticsLoading
                    ? "animate-spin"
                    : "hover:text-purple-800"
                }
              `}
            />
          </button>
        </div>

        <div className="rounded-md">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-4">
              {selectedPost ? (
                <PostDetailsCard
                  post={selectedPost}
                  onBack={() => setSelectedPost(null)}
                />
              ) : (
                <div className="bg-white rounded-md border p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {analytics?.page.name}
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {analytics?.page.category ||
                          analytics?.page.username ||
                          "—"}
                        {" • "}
                        {audienceCount.toLocaleString() || "0"} {audienceLabel}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mt-6">
                    {selectedPlatform === "youtube" &&
                      analytics?.page.totalViews !== undefined && (
                        <StatCard
                          title={t("total_views")}
                          value={analytics?.page.totalViews}
                        />
                      )}

                    {selectedPlatform === "linkedin" &&
                      analytics?.summary.views !== undefined && (
                        <StatCard
                          title={t("impressions")}
                          value={analytics?.summary.views}
                        />
                      )}

                    {selectedPlatform === "tiktok" &&
                      analytics?.summary.video_count !== undefined && (
                        <StatCard
                          title={t("videos")}
                          value={analytics?.summary.video_count}
                        />
                      )}

                    {analytics?.summary.views !== undefined &&
                      !["youtube", "linkedin"].includes(
                        selectedPlatform || ""
                      ) && (
                        <StatCard
                          title={t("views")}
                          value={analytics?.summary.views}
                        />
                      )}

                    {analytics?.summary.likes !== undefined && (
                      <StatCard
                        title={t("likes")}
                        value={analytics?.summary.likes}
                      />
                    )}

                    {analytics?.summary.comments !== undefined && (
                      <StatCard
                        title={t("comments")}
                        value={analytics?.summary.comments}
                      />
                    )}

                    {analytics?.summary.shares !== undefined && (
                      <StatCard
                        title={t("shares")}
                        value={analytics?.summary.shares}
                      />
                    )}

                    {analytics?.summary.clicks !== undefined && (
                      <StatCard
                        title={t("clicks")}
                        value={analytics?.summary.clicks}
                      />
                    )}

                    {analytics?.performance?.engagementRate !== undefined && (
                      <StatCard
                        title={t("engagement_rate")}
                        value={`${(
                          analytics?.performance.engagementRate * 100
                        ).toFixed(2)}%`}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="md:col-span-1">
              <div className="bg-white rounded-md border p-3 h-[50vh] flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">
                    {t("recent_posts")}
                  </h4>
                  {topPosts.length > 0 && (
                    <span className="text-xs text-gray-500">
                      {topPosts.length} posts
                    </span>
                  )}
                </div>

                {topPosts.length === 0 ? (
                  <p className="text-sm text-gray-500 italic flex-1 flex items-center justify-center">
                    {t("no_posts_available_yet")}
                  </p>
                ) : (
                  <div className="space-y-3 overflow-y-auto flex-1">
                    {topPosts.map((post) => (
                      <button
                        key={post.id}
                        onClick={() => setSelectedPost(post)}
                        className="w-full text-left p-3.5 rounded border hover:bg-gray-50 transition-colors"
                      >
                        <p className="text-sm font-medium line-clamp-2">
                          {post.title || `(${t("no_caption")})`}
                        </p>
                        <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-3">
                          {post.views !== undefined && (
                            <span>Views: {post.views}</span>
                          )}
                          {post.likes !== undefined && (
                            <span>Likes: {post.likes}</span>
                          )}
                          {post.comments !== undefined && (
                            <span>Comments: {post.comments}</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ title, value }: { title: string; value: number | string }) {
  return (
    <div className="bg-gradient-to-br rounded-md p-3 text-center border-2 border-purple-100">
      <p className="text-xl md:text-2xl font-bold text-purple-700">
        {typeof value === "number" ? value.toLocaleString() : value || "0"}
      </p>
      <p className="text-xs text-gray-600 mt-1 font-medium">{title}</p>
    </div>
  );
}

function PostDetailsCard({ post, onBack }: { post: any; onBack: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-md border p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">
            {t("post_details")}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {post.created_time
              ? new Date(post.created_time).toLocaleString()
              : "—"}
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
        >
          ← {t("back")}
        </button>
      </div>

      <div className="bg-gray-50 p-5 rounded-md text-gray-800 leading-relaxed whitespace-pre-wrap">
        {post.title || `(${t("no_caption_provided")})`}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
        {post.views !== undefined && (
          <StatCard title={t("views")} value={post.views} />
        )}
        {post.likes !== undefined && (
          <StatCard title={t("likes")} value={post.likes} />
        )}
        {post.comments !== undefined && (
          <StatCard title={t("comments")} value={post.comments} />
        )}
        {post.shares !== undefined && (
          <StatCard title={t("shares")} value={post.shares} />
        )}
        {post.engagement !== undefined && (
          <StatCard title={t("engagement")} value={post.engagement} />
        )}
      </div>
    </div>
  );
}
